import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import { useCallback } from "react";
import { useNotify } from "react-admin";
import { Card, CardContent } from "@material-ui/core";
import classnames from "classnames";
import { CompactFormToolbar } from "../../common/form/CompactFormToolbar";
import { CompactSaveButton } from "../../common/form/CompactSaveButton";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import { TenantRegistrationApiFactory } from "../../../common/data/service-proxies";
import { handleAxiosError } from "../../../common/data/axiosErrorHandler";
import { HookFormTenantRegistrationPicker } from "../../common/form/hook-form/inputs/HookFormTenantRegistrationPicker";

interface Props {
  id: string;
  onSuccess: () => void;
  onFailed?: () => void;
}

const DefaultCompactFormToolbar = () => {
  const {
    formState: { isSubmitting, isValid },
  } = useFormContext();
  return (
    <CompactFormToolbar>
      <CompactSaveButton
        label="Reassign"
        saving={isSubmitting}
        disabled={isSubmitting || !isValid}
      />
    </CompactFormToolbar>
  );
};

type FormValues = {
  id: string;
  parentTenantRegistrationId?: string;
};

export const ReassignTenantForm = ({
  onSuccess,
  onFailed,
  id,
  ...rest
}: Props) => {
  const notify = useNotify();
  const axios = useAxiosInstance();

  const onSubmit = useCallback(
    (data: FormValues) => {
      if (!axios) {
        return Promise.reject("Unable to resolve the axios instance");
      }

      return TenantRegistrationApiFactory(
        undefined,
        axios.defaults.baseURL,
        axios
      )
        .apiAppTenantregistrationReassigntenantPost({
          id,
          parentTenantRegistrationId: data.parentTenantRegistrationId,
        })
        .then(() => {
          onSuccess();
        })
        .catch((e) => {
          if (onFailed) {
            onFailed();
          }

          console.error(e);
          notify(handleAxiosError(e), { type: "error" });
        });
    },
    [axios, onSuccess, onFailed, notify, id]
  );

  const formContext = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      id,
    },
  });

  const { handleSubmit, control } = formContext;

  const handleSubmitInternal: SubmitHandler<FormValues> = (data) => {
    return Promise.resolve(onSubmit(data)).catch((e: Error) => {
      notify(e.message ?? "An unexpected error has occurred", {
        type: "error",
      });
    });
  };

  return (
    <CardContent>
      <FormProvider {...formContext}>
        <form
          className={classnames("simple-form", "form-create-edit-drawer")}
          {...sanitizeRestProps(rest)}
          onSubmit={handleSubmit(handleSubmitInternal, (err) =>
            console.error(err)
          )}
        >
          <Card>
            <CardContent>
              <div className="ra-input">
                <HookFormTenantRegistrationPicker<FormValues>
                  control={control}
                  name="parentTenantRegistrationId"
                  label="Customer"
                  variant="outlined"
                  defaultValue={null}
                  emptyText="(Independent)"
                />
              </div>
            </CardContent>
          </Card>
          <DefaultCompactFormToolbar />
        </form>
      </FormProvider>
    </CardContent>
  );
};

const sanitizeRestProps = ({
  active,
  dirty,
  dirtyFields,
  dirtyFieldsSinceLastSubmit,
  dirtySinceLastSubmit,
  error,
  errors,
  form,
  hasSubmitErrors,
  hasValidationErrors,
  initialValues,
  modified = null,
  modifiedSinceLastSubmit,
  save = null,
  submitError,
  submitErrors,
  submitFailed,
  submitSucceeded,
  submitting,
  touched = null,
  valid,
  values,
  visited = null,
  __versions = null,
  ...props
}: any) => props;
